import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { getOwner } from '@ember/owner';
import { on } from '@ember/modifier';
import { task } from 'ember-concurrency';
import { variation } from 'ember-launch-darkly';
import { and, or, isEqual, eq, not } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import cannot from 'ember-can/helpers/cannot';
import { pick } from '@nullvoxpopuli/ember-composable-helpers';
import set from 'ember-set-helper/helpers/set';
import {
  HdsPageHeader,
  HdsStepperNav,
  HdsBadge,
  HdsFormTextInputField,
  HdsButtonSet,
  HdsButton,
  HdsSeparator,
  HdsLinkInline,
  HdsFormRadioGroup,
  HdsAlert,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  WithErrors,
  LimitBanner,
  FlexGrid,
  Typography,
  FormInputsRegionSelect,
  DisclosureGuide,
} from 'core/components';
import { boxPadding, boxMargin } from 'core/modifiers';
import awsRegions from 'core/utils/aws-region-list';
import { PEERING_SUBMITTED } from 'core/utils/consts/analytics-events/platform';
import {
  TYPE_NETWORK_PEERING,
  TYPE_NETWORK,
} from 'common/utils/cloud-resource-types';
import UpNext from 'networks-common/components/networks/cards/up-next';
import Automate from 'networks-common/components/networks/peerings/aws/create/automate';
import {
  AWS_CONNECTIONS_INFO,
  AWS_DISCLOSURE_GUIDES_PEERING,
} from 'networks-common/utils/aws-connections';
import { AZURE_DISCLOSURE_GUIDES_PEERING } from 'networks-common/utils/azure-connections';
import { CONNECTION_METHODS } from 'networks-common/utils/connection-methods';
import {
  isAwsProviderNetwork,
  isAzureProviderNetwork,
  providerName,
} from 'networks-common/utils/networks';
import Learn from 'networks-common/components/networks/cards/learn';
import ConnectionCards from 'networks-common/components/networks/form-inputs/connection-cards';
import GuidedFlow from 'networks-common/components/networks/peerings/aws/create/automate/guided-flow';
import Aws from 'hcp/components/page/hvn-peerings/create/instructions/aws';
import Azure from 'hcp/components/page/hvn-peerings/create/instructions/azure';
import { PEERING_CREATION_TYPE_AUTOMATE } from 'hcp/controllers/cloud/orgs/detail/projects/detail/hvns/detail/peerings/create';

const {
  awsQuickLink: { key: quickCreate },
} = CONNECTION_METHODS;

/**
 *
 * `PageHvnPeeringsCreate` renders the HVN Peerings Create page.
 *
 *
 * ```
 * <Page::HvnPeerings::Create
 *   @model={{@model}}
 * />
 * ```
 *
 * @class PageHvnPeeringsCreate
 *
 */

/**
 *
 * `model` will be a model containing the network and peerings;
 * @argument model;
 * @type {Object}
 *
 */
export default class PageHvnPeeringsCreate extends Component {
  @service analytics;
  @service api;
  @service abilities;
  @service flashMessages;
  @service intl;
  @service router;
  @service('metadata.local-storage') metadata;

  @tracked errors;
  @tracked id;

  // Guided Flow state
  @tracked connectionMethod = quickCreate;

  // AWS Form fields
  @tracked accountId;
  @tracked vpcId;
  @tracked vpcRegion = 'us-west-2';
  @tracked vpcCidr = '';

  // Azure form fields
  @tracked tenantId;
  @tracked subscriptionId;
  @tracked resourceGroupName;
  @tracked vnetName;
  @tracked allowForwardedTraffic = false;
  @tracked useRemoteGateways = false;

  awsRegions = awsRegions;

  PEERING_CREATION_TYPE_AUTOMATE = PEERING_CREATION_TYPE_AUTOMATE;

  constructor(owner, args) {
    super(owner, args);
    this.vpcRegion = this.args.model.network.location.region.region;
  }

  awsDisclosureGuides = AWS_DISCLOSURE_GUIDES_PEERING;
  azureDisclosureGuides = AZURE_DISCLOSURE_GUIDES_PEERING;

  get resourceType() {
    return TYPE_NETWORK_PEERING;
  }

  get isAws() {
    return isAwsProviderNetwork(this.args.model.network);
  }

  get isAwsQuickCreateLink() {
    return this.connectionMethod === quickCreate;
  }

  get isAzure() {
    return isAzureProviderNetwork(this.args.model.network);
  }

  get provider() {
    return providerName(this.args.model.network);
  }

  save = task(async (evt) => {
    // don't submit the form
    evt.preventDefault();

    let {
      location: { organizationId, projectId },
      id: hvnId,
    } = this.args.model.network;

    let { network } = this.args.model;
    let { id } = this;
    let payload;
    let target;
    // AWS PROVIDER
    if (this.isAws) {
      let { accountId, vpcId, vpcRegion, vpcCidr } = this;
      target = {
        awsTarget: {
          accountId,
          vpcId,
          region: vpcRegion,
          cidr: vpcCidr,
        },
      };
    }
    // AZURE PROVIDER
    if (this.isAzure) {
      const isHubAndSpokeEnabled = variation('hcp-ui-azure-hub-spoke');
      let {
        tenantId,
        subscriptionId,
        resourceGroupName,
        vnetName,
        allowForwardedTraffic,
        useRemoteGateways,
      } = this;
      target = {
        azureTarget: {
          tenantId,
          subscriptionId,
          resourceGroupName,
          vnetName,
          ...(isHubAndSpokeEnabled
            ? { allowForwardedTraffic, useRemoteGateways }
            : {}),
        },
      };
    }

    payload = {
      peering: {
        id,
        hvn: network,
        target: target,
      },
    };

    this.analytics.trackEvent(PEERING_SUBMITTED, payload);

    await this.api.network.createPeering(
      organizationId,
      projectId,
      hvnId,
      payload
    );

    this.flashMessages.success(
      this.intl.t('components.page.hvn-peerings.create.success.title'),
      {
        content: this.intl.t(
          'components.page.hvn-peerings.create.success.message'
        ),
      }
    );

    if (this.isAws) {
      const scope = 'resource';
      const identifier = `project/${projectId}/${TYPE_NETWORK}/${hvnId}`;
      let d = {
        scope,
        identifier,
        metadata: {
          peering: {},
        },
      };

      d.metadata.peering[this.id] = {
        connectionMethod: this.connectionMethod,
        completion: [],
      };

      await this.metadata.set(identifier, d);
    }
    let routeClass = getOwner(this).lookup(
      `route:${AWS_CONNECTIONS_INFO.peering.detail}`
    );
    routeClass?.refresh();

    return this.router.replaceWith(
      AWS_CONNECTIONS_INFO.peering.detail,
      this.id,
      { queryParams: { 'connection-method': this.connectionMethod } }
    );
  });

  changeConnectionMethod = ({ target: { value } }) => {
    this.connectionMethod = value;
  };

  cancel = () => {
    this.analytics.trackEvent(
      'external_ui_hashicorp_network_peering_create_form_cancel_button_clicked',
      this.args.model.network
    );
  };

  <template>
    {{#if (and (variation "hcp-hvn-guided-flow") this.isAws)}}
      <WithErrors @error={{this.save.last.error}} as |FormError FieldError|>
        <CoreLayoutMain>
          <:header>
            <HdsPageHeader as |PH|>
              <PH.Breadcrumb>
                <BreadcrumbsContainer />
              </PH.Breadcrumb>
              <PH.Title @lg="12">
                {{t "components.page.hvn-peerings.create.title"}}
              </PH.Title>
            </HdsPageHeader>
          </:header>
          <:alert>
            <LimitBanner
              @padding="0"
              @variant={{this.resourceType}}
              @count={{@model.peerings.length}}
              as |A|
            >
              <A.LinkStandalone
                @icon="arrow-right"
                @iconPosition="trailing"
                @route="cloud.orgs.detail.projects.detail.hvns.detail.peerings.list"
                @text={{t
                  "components.limit-banner.create-cta.network-peerings"
                }}
              />
            </LimitBanner>
            <FormError />
          </:alert>
          <:body>
            <div>
              <HdsStepperNav
                @currentStep={{0}}
                @isInteractive={{false}}
                @ariaLabel={{t "components.page.hvn-peerings.create.title"}}
                {{boxPadding "0 0 lg 0"}}
                as |S|
              >
                <S.Step>
                  <:title>
                    {{t
                      "components.page.hvn-peerings.create.guided-flow.steps.create.title"
                    }}
                  </:title>
                  <:description>
                    {{t
                      "components.page.hvn-peerings.create.guided-flow.steps.create.description"
                    }}
                  </:description>
                </S.Step>
                <S.Step>
                  <:title>
                    {{t
                      "components.page.hvn-peerings.create.guided-flow.steps.establish.title"
                    }}
                  </:title>
                  <:description>
                    {{t
                      "components.page.hvn-peerings.create.guided-flow.steps.establish.description"
                    }}
                  </:description>
                </S.Step>
                <S.Step>
                  <:title>
                    {{t
                      "components.page.hvn-peerings.create.guided-flow.steps.configure.title"
                    }}
                  </:title>
                  <:description>
                    {{t
                      "components.page.hvn-peerings.create.guided-flow.steps.configure.description"
                    }}
                  </:description>
                </S.Step>
              </HdsStepperNav>
              <div {{boxPadding "0" "0" "xl"}}>
                <Learn />
              </div>
              <FlexGrid as |G|>
                <G.Item @xs="12" @sm="12" @md="8" @lg="8">
                  <Typography @variant="h2">
                    {{t
                      "components.page.hvn-peerings.create.guided-flow.choose-connection"
                    }}
                  </Typography>
                  <ConnectionCards
                    @selectedConnectionMethod={{this.connectionMethod}}
                    @onChange={{this.changeConnectionMethod}}
                    @headerType="h2"
                  />
                </G.Item>
                {{#if this.isAwsQuickCreateLink}}
                  <GuidedFlow
                    @item={{G.Item}}
                    @network={{@model.network}}
                    @peering={{@model.peering}}
                  >
                    <div>
                      <FormInputsRegionSelect
                        @provider={{this.provider}}
                        @regions={{this.awsRegions}}
                        @selectedRegion={{this.vpcRegion}}
                        @onChange={{pick "target.value" (set this "vpcRegion")}}
                        aria-describedby="vpcRegionHelp"
                        id="vpcRegion"
                        name="vpcRegion"
                        value={{this.vpcRegion}}
                        data-test-input
                      >
                        <:label>
                          {{t
                            "components.page.hvn-peerings.create.form.aws.label.vpc-region"
                          }}
                          <HdsBadge
                            @text={{t
                              "components.networks.peerings.aws.automate.guided-flow.automate.create.required"
                            }}
                          />
                        </:label>
                        <:helper-text>
                          {{t
                            "components.page.hvn-peerings.create.form.aws.label.vpc-region-help"
                          }}
                        </:helper-text>
                      </FormInputsRegionSelect>
                    </div>
                  </GuidedFlow>
                {{else}}
                  <G.Item
                    @xs="12"
                    @sm="12"
                    @md="6"
                    @lg="6"
                    {{boxPadding "xl 0 0"}}
                  >
                    <Typography
                      class="hds-typography-body-300 hds-font-weight-semibold hds-foreground-strong"
                      @variant="h2"
                    >
                      {{t
                        "components.networks.peerings.aws.manual.guided-flow.manual.create.title"
                      }}
                    </Typography>
                    <form
                      class="hcp-hds-form"
                      data-test-form
                      {{boxPadding "xl 0 0"}}
                      {{on "submit" this.save.perform}}
                    >
                      {{! HCP peering name }}
                      <div {{boxPadding "0 0 md"}}>
                        <FieldError
                          @field="peeringName"
                          @hdsDelegatedError={{true}}
                          as |err|
                        >
                          <HdsFormTextInputField
                            @extraAriaDescribedBy="peeringNameHelp"
                            @id="peeringName"
                            @isInvalid={{err}}
                            @type="text"
                            @value={{this.id}}
                            @isRequired={{true}}
                            name="peeringName"
                            data-test-peering-name
                            data-test-input
                            {{on "input" (pick "target.value" (set this "id"))}}
                            as |F|
                          >
                            <F.Label for="peeringName">
                              {{t
                                "components.networks.peerings.aws.manual.guided-flow.manual.create.peering-name.title"
                              }}
                            </F.Label>
                            <F.HelperText id="peerningNameHelp">
                              {{t
                                "components.networks.peerings.aws.manual.guided-flow.manual.create.peering-name.help-text"
                              }}
                            </F.HelperText>
                            {{#if err}}
                              <F.Error>
                                {{err.description}}
                              </F.Error>
                            {{/if}}
                          </HdsFormTextInputField>
                        </FieldError>
                      </div>
                      {{! Account ID }}
                      <div data-test-disclosure-aws {{boxPadding "0 0 md"}}>
                        <FieldError
                          @field="target.Value.AwsTarget.account_id"
                          @hdsDelegatedError={{true}}
                          as |err|
                        >
                          <HdsFormTextInputField
                            @extraAriaDescribedBy="accountIdHelp"
                            @id="accountId"
                            @isInvalid={{err}}
                            @type="text"
                            @value={{this.accountId}}
                            @isRequired={{true}}
                            name="accountId"
                            placeholder={{t
                              "components.page.hvn-peerings.create.form.aws.label.aws-account-id-placeholder"
                            }}
                            data-test-id
                            data-test-input
                            {{on
                              "input"
                              (pick "target.value" (set this "accountId"))
                            }}
                            as |F|
                          >
                            <F.Label for="accountId">
                              {{t
                                "components.networks.peerings.aws.manual.guided-flow.manual.create.account-id.title"
                              }}
                            </F.Label>
                            <F.HelperText id="accountIdHelp">
                              {{t
                                "components.networks.peerings.aws.manual.guided-flow.manual.create.account-id.help-text"
                                htmlSafe=true
                              }}
                            </F.HelperText>
                            {{#if err}}
                              <F.Error>
                                {{err.description}}
                              </F.Error>
                            {{/if}}
                          </HdsFormTextInputField>
                        </FieldError>
                        <DisclosureGuide
                          @content={{this.awsDisclosureGuides.accountId}}
                          data-test-disclosure-guide-aws-account-id
                        />
                      </div>
                      {{! VPC Region }}
                      <div {{boxPadding "0 0 md"}}>
                        {{! TODO TCE-1606 Peering Create }}
                        <FormInputsRegionSelect
                          @provider={{this.provider}}
                          @regions={{this.awsRegions}}
                          @selectedRegion={{this.vpcRegion}}
                          @onChange={{pick
                            "target.value"
                            (set this "vpcRegion")
                          }}
                          aria-describedby="vpcRegionHelp"
                          id="vpcRegion"
                          name="vpcRegion"
                          class="select"
                          value={{this.vpcRegion}}
                          data-test-input
                        >
                          <:label>
                            {{t
                              "components.page.hvn-peerings.create.form.aws.label.vpc-region"
                            }}
                            <HdsBadge
                              @size="small"
                              @text={{t
                                "components.networks.peerings.aws.manual.guided-flow.manual.create.required"
                              }}
                            />
                          </:label>
                          <:helper-text>
                            {{t
                              "components.networks.peerings.aws.manual.guided-flow.manual.create.vpc-region.help-text"
                            }}
                          </:helper-text>
                        </FormInputsRegionSelect>
                      </div>
                      {{! VPC ID }}
                      <div data-test-disclosure-aws {{boxPadding "0 0 md"}}>
                        <FieldError
                          @field="target.Value.AwsTarget.vpc_id"
                          @hdsDelegatedError={{true}}
                          as |err|
                        >
                          <HdsFormTextInputField
                            @extraAriaDescribedBy="vpcIdHelp"
                            @id="vpcId"
                            @isInvalid={{err}}
                            @type="text"
                            @value={{this.vpcId}}
                            @isRequired={{true}}
                            name="vpcId"
                            placeholder={{t
                              "components.page.hvn-peerings.create.form.aws.label.vpc-id-placeholder"
                            }}
                            data-test-id
                            data-test-input
                            {{on
                              "input"
                              (pick "target.value" (set this "vpcId"))
                            }}
                            as |F|
                          >
                            <F.Label for="vpcId">
                              {{t
                                "components.page.hvn-peerings.create.form.aws.label.vpc-id"
                              }}
                            </F.Label>
                            <F.HelperText id="vpcIdHelp">
                              {{t
                                "components.networks.peerings.aws.manual.guided-flow.manual.create.vpc-id.help-text"
                                vpcRegion=this.vpcRegion
                                htmlSafe=true
                              }}
                            </F.HelperText>
                            {{#if err}}
                              <F.Error>
                                {{err.description}}
                              </F.Error>
                            {{/if}}
                          </HdsFormTextInputField>
                        </FieldError>
                        <DisclosureGuide
                          @content={{this.awsDisclosureGuides.vpcId}}
                        />
                      </div>
                      {{! VPC CIDR Block}}
                      <div data-test-disclosure-aws {{boxPadding "0 0 md"}}>
                        <HdsFormTextInputField
                          @id="vpcCidrBlock"
                          @type="text"
                          @value={{this.vpcCidr}}
                          @isRequired={{true}}
                          name="vpcCidrBlock"
                          placeholder={{t
                            "components.page.hvn-peerings.create.form.aws.label.vpc-cidr-block-placeholder"
                          }}
                          data-test-id
                          data-test-input
                          {{on
                            "input"
                            (pick "target.value" (set this "vpcCidr"))
                          }}
                          as |F|
                        >
                          <F.Label>
                            {{t
                              "components.page.hvn-peerings.create.form.aws.label.vpc-cidr-block"
                            }}
                          </F.Label>
                          <F.HelperText>
                            {{t
                              "components.page.hvn-peerings.create.form.aws.label.vpc-cidr-block-help"
                              htmlSafe=true
                              hvnCidr=@model.network.cidrBlock
                            }}
                          </F.HelperText>
                        </HdsFormTextInputField>
                        <DisclosureGuide
                          @content={{this.awsDisclosureGuides.cidrBlock}}
                        />
                      </div>
                      <footer
                        aria-label={{t
                          "components.page.hvn-peerings.create.guided-flow.steps.create.footer-aria-label"
                        }}
                      >
                        <section {{boxPadding "lg 0 0"}}>
                          <HdsButtonSet>
                            <HdsButton
                              @color="primary"
                              @text={{t
                                "components.page.hvn-peerings.create.action"
                              }}
                              disabled={{or
                                (cannot
                                  "create network-peering" @model.peerings
                                )
                                this.save.isRunning
                              }}
                              type="submit"
                              data-test-submit
                            />
                            <HdsButton
                              @color="secondary"
                              @route="cloud.orgs.detail.projects.detail.hvns.detail.peerings.list"
                              @text={{t "components.form.cancel"}}
                              data-test-cancel
                              {{on "click" this.cancel}}
                            />
                          </HdsButtonSet>
                        </section>
                      </footer>
                    </form>
                  </G.Item>
                {{/if}}
              </FlexGrid>
            </div>
          </:body>
        </CoreLayoutMain>
      </WithErrors>
    {{else}}
      <WithErrors @error={{this.save.last.error}} as |FormError FieldError|>
        <CoreLayoutMain>
          <:header>
            <HdsPageHeader as |PH|>
              <PH.Breadcrumb>
                <BreadcrumbsContainer />
              </PH.Breadcrumb>
              <PH.Title @lg="8">
                {{t
                  (if
                    (isEqual
                      @peeringCreationType this.PEERING_CREATION_TYPE_AUTOMATE
                    )
                    "components.page.hvn-peerings.create.automate.title"
                    "components.page.hvn-peerings.create.title"
                  )
                }}
              </PH.Title>
            </HdsPageHeader>
          </:header>
          <:alert>
            <LimitBanner
              @padding="0"
              @variant={{this.resourceType}}
              @count={{@model.peerings.length}}
              as |A|
            >
              <A.LinkStandalone
                @icon="arrow-right"
                @iconPosition="trailing"
                @route="cloud.orgs.detail.projects.detail.hvns.detail.peerings.list"
                @text={{t
                  "components.limit-banner.create-cta.network-peerings"
                }}
              />
            </LimitBanner>
            <FormError />
          </:alert>
          <:body>
            <div>
              <FlexGrid as |G|>
                {{#if
                  (eq @peeringCreationType this.PEERING_CREATION_TYPE_AUTOMATE)
                }}
                  <G.Item @xs="12" @sm="8" @md="8" @lg="6">
                    <div {{boxPadding "xl 0 0 0"}}>
                      <FormInputsRegionSelect
                        @provider={{this.provider}}
                        @regions={{this.awsRegions}}
                        @selectedRegion={{this.vpcRegion}}
                        @onChange={{pick "target.value" (set this "vpcRegion")}}
                        aria-describedby="vpcRegionHelp"
                        id="vpcRegion"
                        name="vpcRegion"
                        value={{this.vpcRegion}}
                        data-test-input
                      >
                        <:label>
                          {{t
                            "components.page.hvn-peerings.create.form.aws.label.vpc-region"
                          }}
                        </:label>
                        <:helper-text>
                          {{t
                            "components.page.hvn-peerings.create.form.aws.label.vpc-region-help"
                          }}
                        </:helper-text>
                      </FormInputsRegionSelect>
                    </div>
                  </G.Item>
                  <Automate @item={{G.Item}} @network={{@model.network}} />
                {{else}}
                  <G.Item @xs="12" @sm="12" @md="8" @lg="5">
                    <form
                      aria-label={{t
                        "components.networks.peerings.aws.manual.form-aria-label"
                      }}
                      class="hcp-hds-form"
                      data-test-form
                      {{on "submit" this.save.perform}}
                    >
                      <Typography
                        @variant="body1"
                        class="hds-typography-body-200 hds-font-weight-regular hds-foreground-primary"
                        {{boxMargin "0 0 md 0"}}
                      >
                        {{!
                XXX: Potentially unreachable code.
                The expression for checking if this is an automated workflow
                creation needs to be re-visited. This code branch will never
                have the peering conntion be automated.
                }}
                        {{t
                          (if
                            (isEqual
                              @peeringCreationType
                              this.PEERING_CREATION_TYPE_AUTOMATE
                            )
                            "components.page.hvn-peerings.create.automate.description.message"
                            "components.page.hvn-peerings.create.manual.description.message"
                          )
                          provider=this.provider
                          htmlSafe=true
                        }}
                      </Typography>

                      {{!
              XXX: Potentially unreachable code.
              The expression for checking if this is an automated workflow
              creation needs to be re-visited. This code branch will never
              have the peering conntion be automated.
              }}
                      {{#if
                        (and
                          this.isAws
                          (not
                            (isEqual
                              @peeringCreationType
                              this.PEERING_CREATION_TYPE_AUTOMATE
                            )
                          )
                        )
                      }}
                        <div {{boxPadding "0 0 lg 0"}}>
                          <Aws
                            @provider={{this.provider}}
                            @peeringCreationType={{@peeringCreationType}}
                          />
                        </div>
                        <HdsSeparator />
                      {{else if this.isAzure}}
                        <div {{boxPadding "0 0 lg 0"}}>
                          <Azure @provider={{this.provider}} />
                        </div>
                        <HdsSeparator />
                      {{/if}}

                      <section {{boxPadding "md 0 0 0"}}>
                        <header
                          aria-label={{t
                            "components.networks.peerings.aws.manual.header-aria-label"
                          }}
                        >
                          <Typography
                            @variant="h3"
                            class="hds-typography-display-300 hds-font-weight-bold"
                            {{boxMargin "0 0 lg 0"}}
                          >
                            {{#if this.isAws}}
                              {{t
                                "components.page.hvn-peerings.create.form.aws.header.aws-vpc"
                              }}
                              <HdsLinkInline
                                @href="https://docs.aws.amazon.com/vpc/latest/peering/what-is-vpc-peering.html"
                                @icon="external-link"
                                @iconPosition="trailing"
                                @isHrefExternal={{true}}
                                class="hds-typography-body-100"
                                {{boxMargin "0 0 0 lg"}}
                              >
                                {{t
                                  "components.page.hvn-peerings.create.form.aws.header.aws-vpc-documentation-link"
                                }}
                              </HdsLinkInline>
                            {{else if this.isAzure}}
                              {{t
                                "components.page.hvn-peerings.create.form.azure.header.azure-vnet"
                              }}
                              {{! TODO: HCPE-1753 }}
                              {{!--
                    <Hds::Link::Inline @href='https://docs.aws.amazon.com/vpc/latest/peering/what-is-vpc-peering.html'
                      @icon='external-link' @iconPosition='trailing' @isHrefExternal={{true}}
                      class='hds-typography-body-100'>
                      {{t 'components.page.hvn-peerings.create.form.azure.header.azure-vnet-documentation-link'}}
                    </Hds::Link::Inline>
                    --}}
                            {{/if}}
                          </Typography>
                        </header>

                        <FieldError
                          @field="id"
                          @hdsDelegatedError={{true}}
                          as |err|
                        >
                          <HdsFormTextInputField
                            @id="id"
                            @isInvalid={{err}}
                            @type="text"
                            @value={{this.id}}
                            name="id"
                            data-test-input
                            {{on "input" (pick "target.value" (set this "id"))}}
                            as |F|
                          >
                            <F.Label>
                              {{t
                                "components.page.hvn-peerings.create.form.label.connection-id"
                              }}
                            </F.Label>
                            <F.HelperText>
                              {{t
                                "components.page.hvn-peerings.create.form.label.connection-id-help"
                              }}
                            </F.HelperText>
                            {{#if err}}
                              <F.Error>
                                {{err.description}}
                              </F.Error>
                            {{/if}}
                          </HdsFormTextInputField>
                        </FieldError>

                        <br />

                        {{#if this.isAws}}
                          <div data-test-disclosure-aws>
                            <FieldError
                              @field="target.Value.AwsTarget.account_id"
                              @hdsDelegatedError={{true}}
                              as |err|
                            >
                              <HdsFormTextInputField
                                @id="accountId"
                                @isInvalid={{err}}
                                @type="text"
                                @value={{this.accountId}}
                                name="accountId"
                                placeholder={{t
                                  "components.page.hvn-peerings.create.form.aws.label.aws-account-id-placeholder"
                                }}
                                data-test-input
                                {{on
                                  "input"
                                  (pick "target.value" (set this "accountId"))
                                }}
                                as |F|
                              >
                                <F.Label>
                                  {{t
                                    "components.page.hvn-peerings.create.form.aws.label.aws-account-id"
                                    htmlSafe=true
                                  }}
                                </F.Label>
                                <F.HelperText>
                                  {{t
                                    "components.page.hvn-peerings.create.form.aws.label.aws-account-id-help"
                                    htmlSafe=true
                                  }}
                                </F.HelperText>
                                {{#if err}}
                                  <F.Error>
                                    {{err.description}}
                                  </F.Error>
                                {{/if}}
                              </HdsFormTextInputField>
                            </FieldError>
                            <DisclosureGuide
                              @content={{this.awsDisclosureGuides.accountId}}
                              data-test-disclosure-guide-aws-account-id
                            />
                          </div>

                          <br />

                          <div>
                            {{! TODO TCE-1606 Peering Create }}
                            <FormInputsRegionSelect
                              @provider={{this.provider}}
                              @regions={{this.awsRegions}}
                              @selectedRegion={{this.vpcRegion}}
                              @onChange={{pick
                                "target.value"
                                (set this "vpcRegion")
                              }}
                              aria-describedby="vpcRegionHelp"
                              id="vpcRegion"
                              name="vpcRegion"
                              value={{this.vpcRegion}}
                              data-test-input
                            >
                              <:label>
                                {{t
                                  "components.page.hvn-peerings.create.form.aws.label.vpc-region"
                                }}
                              </:label>
                              <:helper-text>
                                {{t
                                  "components.page.hvn-peerings.create.form.aws.label.vpc-region-help"
                                }}
                              </:helper-text>
                            </FormInputsRegionSelect>
                          </div>

                          <br />

                          <div data-test-disclosure-aws>
                            <FieldError
                              @field="target.Value.AwsTarget.vpc_id"
                              @hdsDelegatedError={{true}}
                              as |err|
                            >
                              <HdsFormTextInputField
                                @id="vpcId"
                                @isInvalid={{err}}
                                @type="text"
                                @value={{this.vpcId}}
                                name="vpcId"
                                placeholder={{t
                                  "components.page.hvn-peerings.create.form.aws.label.vpc-id-placeholder"
                                }}
                                data-test-input
                                {{on
                                  "input"
                                  (pick "target.value" (set this "vpcId"))
                                }}
                                as |F|
                              >
                                <F.Label>
                                  {{t
                                    "components.page.hvn-peerings.create.form.aws.label.vpc-id"
                                  }}
                                </F.Label>
                                <F.HelperText>
                                  {{t
                                    "components.page.hvn-peerings.create.form.aws.label.vpc-id-help"
                                    vpcRegion=this.vpcRegion
                                    htmlSafe=true
                                  }}
                                </F.HelperText>
                                {{#if err}}
                                  <F.Error>
                                    {{err.description}}
                                  </F.Error>
                                {{/if}}
                              </HdsFormTextInputField>
                            </FieldError>
                            <DisclosureGuide
                              @content={{this.awsDisclosureGuides.vpcId}}
                            />
                          </div>

                          <br />

                          {{#if (variation "hcp-consul-activation")}}
                            <div data-test-disclosure-aws>
                              <FieldError
                                @field="target.Value.AwsTarget.cidr"
                                @hdsDelegatedError={{true}}
                                as |err|
                              >
                                <HdsFormTextInputField
                                  @id="vpcCidrBlock"
                                  @isInvalid={{err}}
                                  @type="text"
                                  @value={{this.vpcCidr}}
                                  name="vpcCidrBlock"
                                  placeholder={{t
                                    "components.page.hvn-peerings.create.form.aws.label.vpc-cidr-block-placeholder"
                                  }}
                                  data-test-input
                                  {{on
                                    "input"
                                    (pick "target.value" (set this "vpcCidr"))
                                  }}
                                  as |F|
                                >
                                  <F.Label>
                                    {{t
                                      "components.page.hvn-peerings.create.form.aws.label.vpc-cidr-block"
                                    }}
                                  </F.Label>
                                  <F.HelperText>
                                    {{t
                                      "components.page.hvn-peerings.create.form.aws.label.vpc-cidr-block-help"
                                      htmlSafe=true
                                      hvnCidr=@model.network.cidrBlock
                                    }}
                                    {{!-- <b data-test-hvn-cidr-details>
                          {{t
                          'components.page.hvn-peerings.create.form.aws.label.vpc-cidr-of-hvn'
                          hvnID=@model.network.id
                          hvnCidr=@model.network.cidrBlock
                          }}
                        </b> --}}
                                  </F.HelperText>
                                  {{#if err}}
                                    <F.Error>
                                      {{err.description}}
                                    </F.Error>
                                  {{/if}}
                                </HdsFormTextInputField>
                              </FieldError>
                              <DisclosureGuide
                                @content={{this.awsDisclosureGuides.cidrBlock}}
                              />
                            </div>
                          {{/if}}
                        {{else if this.isAzure}}
                          <div>
                            <FieldError
                              @field="target.Value.AzureTarget.tenant_id"
                              @hdsDelegatedError={{true}}
                              as |err|
                            >
                              <HdsFormTextInputField
                                @id="tenantId"
                                @isInvalid={{err}}
                                @type="text"
                                @value={{this.tenantId}}
                                name="tenantId"
                                placeholder={{t
                                  "components.page.hvn-peerings.create.form.azure.label.tenant-id-placeholder"
                                }}
                                data-test-input
                                {{on
                                  "input"
                                  (pick "target.value" (set this "tenantId"))
                                }}
                                as |F|
                              >
                                <F.Label>
                                  {{t
                                    "components.page.hvn-peerings.create.form.azure.label.tenant-id"
                                  }}
                                </F.Label>
                                <F.HelperText>
                                  {{t
                                    "components.page.hvn-peerings.create.form.azure.label.tenant-id-help"
                                  }}
                                </F.HelperText>
                                {{#if err}}
                                  <F.Error>
                                    {{err.description}}
                                  </F.Error>
                                {{/if}}
                              </HdsFormTextInputField>
                            </FieldError>
                            <DisclosureGuide
                              @content={{this.azureDisclosureGuides.tenantId}}
                            />
                          </div>

                          <br />

                          <div>
                            <FieldError
                              @field="target.Value.AzureTarget.subscription_id"
                              @hdsDelegatedError={{true}}
                              as |err|
                            >
                              <HdsFormTextInputField
                                @id="subscriptionId"
                                @isInvalid={{err}}
                                @type="text"
                                @value={{this.subscriptionId}}
                                name="subscriptionId"
                                placeholder={{t
                                  "components.page.hvn-peerings.create.form.azure.label.subscription-id-placeholder"
                                }}
                                data-test-input
                                {{on
                                  "input"
                                  (pick
                                    "target.value" (set this "subscriptionId")
                                  )
                                }}
                                as |F|
                              >
                                <F.Label>
                                  {{t
                                    "components.page.hvn-peerings.create.form.azure.label.subscription-id"
                                  }}
                                </F.Label>
                                <F.HelperText>
                                  {{t
                                    "components.page.hvn-peerings.create.form.azure.label.subscription-id-help"
                                  }}
                                </F.HelperText>
                                {{#if err}}
                                  <F.Error>
                                    {{err.description}}
                                  </F.Error>
                                {{/if}}
                              </HdsFormTextInputField>
                            </FieldError>
                            <DisclosureGuide
                              @content={{this.azureDisclosureGuides.subscriptionId}}
                            />
                          </div>

                          <br />

                          <div>
                            <FieldError
                              @field="target.Value.AzureTarget.resource_group_name"
                              @hdsDelegatedError={{true}}
                              as |err|
                            >
                              <HdsFormTextInputField
                                @id="resourceGroupName"
                                @isInvalid={{err}}
                                @type="text"
                                @value={{this.resourceGroupName}}
                                name="resourceGroupName"
                                placeholder={{t
                                  "components.page.hvn-peerings.create.form.azure.label.resource-group-name-placeholder"
                                }}
                                data-test-input
                                {{on
                                  "input"
                                  (pick
                                    "target.value"
                                    (set this "resourceGroupName")
                                  )
                                }}
                                as |F|
                              >
                                <F.Label>
                                  {{t
                                    "components.page.hvn-peerings.create.form.azure.label.resource-group-name"
                                  }}
                                </F.Label>
                                <F.HelperText>
                                  {{t
                                    "components.page.hvn-peerings.create.form.azure.label.resource-group-name-help"
                                  }}
                                </F.HelperText>
                                {{#if err}}
                                  <F.Error>
                                    {{err.description}}
                                  </F.Error>
                                {{/if}}
                              </HdsFormTextInputField>
                            </FieldError>
                            <DisclosureGuide
                              @content={{this.azureDisclosureGuides.resourceGroupName}}
                            />
                          </div>

                          <br />

                          <div>
                            <FieldError
                              @field="target.Value.AzureTarget.vnet_name"
                              @hdsDelegatedError={{true}}
                              as |err|
                            >
                              <HdsFormTextInputField
                                @id="vnetName"
                                @isInvalid={{err}}
                                @type="text"
                                @value={{this.vnetName}}
                                name="vnetName"
                                placeholder={{t
                                  "components.page.hvn-peerings.create.form.azure.label.vnet-name-placeholder"
                                }}
                                data-test-input
                                {{on
                                  "input"
                                  (pick "target.value" (set this "vnetName"))
                                }}
                                as |F|
                              >
                                <F.Label>
                                  {{t
                                    "components.page.hvn-peerings.create.form.azure.label.vnet-name"
                                  }}
                                </F.Label>
                                <F.HelperText>
                                  {{t
                                    "components.page.hvn-peerings.create.form.azure.label.vnet-name-help"
                                  }}
                                </F.HelperText>
                                {{#if err}}
                                  <F.Error>
                                    {{err.description}}
                                  </F.Error>
                                {{/if}}
                              </HdsFormTextInputField>
                            </FieldError>
                            <DisclosureGuide
                              @content={{this.azureDisclosureGuides.vnetName}}
                            />
                          </div>

                          {{#if (variation "hcp-ui-azure-hub-spoke")}}
                            <br />
                            <FieldError
                              @field="target.Value.AzureTarget.allow_forwarded_traffic"
                              @hdsDelegatedError={{true}}
                              as |err|
                            >
                              <HdsFormRadioGroup
                                @layout="vertical"
                                @name="allowForwardedTraffic"
                                data-test-allow-forwarded-traffic-radio-group
                                data-test-radio-group
                                as |G|
                              >
                                <G.Legend>{{t
                                    "components.page.hvn-peerings.create.form.azure.label.allow-forwarded-traffic"
                                  }}
                                </G.Legend>
                                <G.HelperText>
                                  {{t
                                    "components.page.hvn-peerings.create.form.azure.label.allow-forwarded-traffic-help"
                                  }}
                                </G.HelperText>
                                <G.RadioField
                                  checked={{not this.allowForwardedTraffic}}
                                  @value={{false}}
                                  {{on
                                    "change"
                                    (set this "allowForwardedTraffic" false)
                                  }}
                                  data-test-allow-forwarded-traffic-disallowed
                                  as |F|
                                >
                                  <F.Label>{{t
                                      "components.page.hvn-peerings.create.form.azure.label.disallow"
                                    }}
                                  </F.Label>
                                </G.RadioField>
                                <G.RadioField
                                  checked={{this.allowForwardedTraffic}}
                                  @value={{true}}
                                  {{on
                                    "change"
                                    (set this "allowForwardedTraffic" true)
                                  }}
                                  data-test-allow-forwarded-traffic-allowed
                                  as |F|
                                >
                                  <F.Label>{{t
                                      "components.page.hvn-peerings.create.form.azure.label.allow"
                                    }}
                                  </F.Label>
                                </G.RadioField>
                                {{#if err}}
                                  <G.Error>{{err.description}}</G.Error>
                                {{/if}}
                              </HdsFormRadioGroup>
                            </FieldError>
                            <br />
                            <FieldError
                              @field="target.Value.AzureTarget.use_remote_gateways"
                              @hdsDelegatedError={{true}}
                              as |err|
                            >
                              <HdsFormRadioGroup
                                @layout="vertical"
                                @name="useRemoteGateways"
                                data-test-use-remote-gateways-radio-group
                                data-test-radio-group
                                as |G|
                              >
                                <G.Legend>{{t
                                    "components.page.hvn-peerings.create.form.azure.label.use-remote-gateways"
                                  }}
                                </G.Legend>
                                <G.HelperText>
                                  {{t
                                    "components.page.hvn-peerings.create.form.azure.label.allow-forwarded-traffic-help"
                                  }}
                                </G.HelperText>
                                <G.RadioField
                                  checked={{not this.useRemoteGateways}}
                                  @value={{false}}
                                  {{on
                                    "change"
                                    (set this "useRemoteGateways" false)
                                  }}
                                  data-test-use-remote-gateways-disallowed
                                  as |F|
                                >
                                  <F.Label>{{t
                                      "components.page.hvn-peerings.create.form.azure.label.disallow"
                                    }}</F.Label>
                                </G.RadioField>
                                <G.RadioField
                                  checked={{this.useRemoteGateways}}
                                  @value={{true}}
                                  {{on
                                    "change"
                                    (set this "useRemoteGateways" true)
                                  }}
                                  data-test-use-remote-gateways-allowed
                                  as |F|
                                >
                                  <F.Label>{{t
                                      "components.page.hvn-peerings.create.form.azure.label.allow"
                                    }}</F.Label>
                                </G.RadioField>
                                {{#if err}}
                                  <G.Error>{{err.description}}</G.Error>
                                {{/if}}
                              </HdsFormRadioGroup>
                            </FieldError>

                            <br />

                            <HdsAlert @type="inline" as |A|>
                              <A.Description>
                                {{t
                                  "components.page.hvn-peerings.create.form.azure.alert.hub-spoke"
                                  htmlSafe=true
                                }}
                              </A.Description>
                            </HdsAlert>
                          {{/if}}
                        {{/if}}
                      </section>

                      <HdsSeparator />

                      {{#if (variation "hcp-ui-hvn-activation")}}
                        <UpNext @resourceType={{this.resourceType}} />
                      {{/if}}
                      <footer
                        aria-label={{t
                          "components.networks.peerings.aws.manual.footer-aria-label"
                        }}
                      >
                        <HdsButtonSet>
                          <HdsButton
                            @color="primary"
                            @text={{t
                              "components.page.hvn-peerings.create.action"
                            }}
                            disabled={{or
                              (cannot "create network-peering" @model.peerings)
                              this.save.isRunning
                            }}
                            type="submit"
                            data-test-submit
                          />
                          <HdsButton
                            @color="secondary"
                            @route="cloud.orgs.detail.projects.detail.hvns.detail.peerings.list"
                            @text={{t "components.form.cancel"}}
                            data-test-cancel
                            {{on "click" this.cancel}}
                          />
                        </HdsButtonSet>
                      </footer>
                    </form>
                  </G.Item>
                {{/if}}
              </FlexGrid>
            </div>
          </:body>
        </CoreLayoutMain>
      </WithErrors>
    {{/if}}
  </template>
}
